import { useEffect, useRef } from 'react';
import { councilWsClient } from '../services/councilWsClient';

/**
 * Wire a full message re-pull to the events that mean "the spine may have
 * changed since we last painted": `turn_added` (a peer's turn landed),
 * `ws_connected` (reconnect — events may have been missed while away),
 * `merge_turn_failed` (C2.2 — a fold-back synthesis failed; reload clears the
 * Camp's transient pending state back to server truth), and `peer_turn_failed`
 * (§3 F7 — a failed fork/peer turn; reload flips the camp to 'failed' so it doesn't
 * zombie as "reading the trail…"). Server-truth reload via
 * ConversationContext.reloadMessages (NOT optimistic patching).
 *
 * reloadMessages is not memoized in the provider, so we hold it in a ref and key
 * the effect on sessionId only — no re-subscribe churn on every render.
 */
export function useReloadOnPeerEvent(
  sessionId: string | null | undefined,
  reloadMessages: () => Promise<void>,
): void {
  const reloadRef = useRef(reloadMessages);
  reloadRef.current = reloadMessages;

  useEffect(() => {
    if (!sessionId) return;
    const unsub = councilWsClient.subscribe(sessionId, (event) => {
      const t = (event.event_type as unknown as string);
      if (t === 'turn_added' || t === 'ws_connected' || t === 'merge_turn_failed' || t === 'peer_turn_failed' || t === 'divergence_promoted') {
        void reloadRef.current();
      }
    });
    return unsub;
  }, [sessionId]);
}
